import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  invalidateThreadList,
  useThreadsApiClient,
} from '@/entities/thread';
import { useChatViewStore } from './viewStore';
import { useChatWorkspaceStore } from './workspaceStore';
import { useChatLockStore } from './lockStore';

const pendingNewChats = new Map<string, Promise<string>>();

type WorkspaceGeneration = {
  userId: string;
  loginSessionId: string;
  resetGeneration: number;
};

function workspaceGeneration(): WorkspaceGeneration {
  const workspace = useChatWorkspaceStore.getState();
  return {
    userId: workspace.userId,
    loginSessionId: workspace.loginSessionId,
    resetGeneration: workspace.resetGeneration,
  };
}

function workspaceGenerationIsCurrent(expected: WorkspaceGeneration): boolean {
  const current = workspaceGeneration();
  return current.userId === expected.userId
    && current.loginSessionId === expected.loginSessionId
    && current.resetGeneration === expected.resetGeneration;
}

function readCreatedThreadId(raw: unknown): string {
  const data = typeof raw === 'object' && raw !== null
    ? raw as { session_id?: unknown }
    : {};
  if (typeof data.session_id !== 'string' || data.session_id.length === 0) {
    throw new Error('New chat response did not include session_id');
  }
  return data.session_id;
}

export function useChatWorkspaceController() {
  const queryClient = useQueryClient();
  const threadsApi = useThreadsApiClient();

  const selectAgent = useCallback((agentId: string | null) => {
    useChatViewStore.getState().selectAgent(agentId);
  }, []);

  const selectThread = useCallback((agentId: string, threadId: string | null) => {
    useChatViewStore.getState().selectThread(agentId, threadId);
    if (threadId !== null) {
      useChatWorkspaceStore.getState().markThreadActive(agentId, threadId, true);
    }
  }, []);

  const clearThread = useCallback((agentId: string, expectedThreadId?: string | null) => {
    useChatViewStore.getState().selectThread(agentId, null);
    useChatWorkspaceStore.getState().clearActiveThread(agentId, expectedThreadId ?? null);
  }, []);

  const startNewChat = useCallback(async (agentId: string): Promise<string> => {
    const generation = workspaceGeneration();
    const key = JSON.stringify([
      generation.userId,
      generation.loginSessionId,
      generation.resetGeneration,
      agentId,
    ]);
    const pending = pendingNewChats.get(key);
    if (pending) return pending;

    const task = (async () => {
      useChatWorkspaceStore.getState().setCreatingNewChat(agentId, true);
      try {
        const threadId = readCreatedThreadId(await threadsApi.create(agentId));
        if (!workspaceGenerationIsCurrent(generation)) return threadId;
        useChatWorkspaceStore.getState().markThreadActive(agentId, threadId, true);
        useChatViewStore.getState().selectThread(agentId, threadId);
        await invalidateThreadList(queryClient);
        return threadId;
      } finally {
        if (workspaceGenerationIsCurrent(generation)) {
          useChatWorkspaceStore.getState().setCreatingNewChat(agentId, false);
        }
        pendingNewChats.delete(key);
      }
    })();

    pendingNewChats.set(key, task);
    return task;
  }, [queryClient, threadsApi]);

  const resetWorkspace = useCallback(() => {
    useChatLockStore.getState().clearAll();
    useChatWorkspaceStore.getState().resetWorkspace();
    useChatViewStore.getState().clearAllState(useChatWorkspaceStore.getState().resetGeneration);
  }, []);

  const clearSelection = useCallback(() => {
    useChatViewStore.getState().clearSelection();
  }, []);

  const pruneAgents = useCallback((agentIds: readonly string[]) => {
    useChatViewStore.getState().pruneAgents(agentIds);
    useChatWorkspaceStore.getState().pruneAgents(agentIds);
  }, []);

  return {
    selectAgent,
    selectThread,
    clearThread,
    startNewChat,
    resetWorkspace,
    clearSelection,
    pruneAgents,
  };
}

export function useResolvedThreadId(agentId: string): string | null {
  return useChatViewStore((state) => state.agentThreads[agentId] ?? null);
}
